/**
 * annique-issues#152 — "Continue shopping" on the FILLED cart page.
 *
 * The cart DRAWER has always had one (sections/cart-drawer.liquid, wrapped in
 * <dialog-close-button>, which just closes the drawer). A drawer can close; a
 * PAGE has to navigate, and there is no honest "previous screen" on a page —
 * the shopper may have arrived from a bookmark, an email, a new tab, or a
 * search engine.
 *
 * So this is progressive enhancement over a REAL link, never a bare button:
 *
 *   - The Liquid renders <a href="{{ settings.cart_empty_cart_link }}"> — the
 *     exact target the EMPTY cart state already uses. That works with no JS, on
 *     middle-click, on ctrl/cmd-click, and it is crawlable.
 *   - This element upgrades a plain left-click to history.back() ONLY when the
 *     previous entry is provably a same-origin page that is not the cart or
 *     checkout itself. Otherwise the href fires unchanged.
 *
 * Never a dead button: every path either navigates back or follows the href.
 *
 * Refresh is safe — browsers preserve document.referrer across a reload, and a
 * reload REPLACES the current history entry, so back() still lands on the true
 * previous screen rather than on the pre-refresh cart.
 */
(function () {
  'use strict';

  // --- continue-shopping guards ---
  function root() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root;
      }
    } catch (e) {}
    return '/';
  }

  // A referrer we are willing to go back to: same origin, and not a cart or
  // checkout URL (going "back" to the cart from the cart is a no-op the shopper
  // reads as a broken button). Market/locale prefixes are handled by measuring
  // the path relative to Shopify.routes.root.
  function referrerIsUsable() {
    var ref = '';
    try { ref = document.referrer || ''; } catch (e) { return false; }
    if (!ref) return false;

    var url;
    try { url = new URL(ref, window.location.href); } catch (e) { return false; }
    if (url.origin !== window.location.origin) return false;

    var prefix = root();
    var path = url.pathname;
    if (prefix !== '/' && path.indexOf(prefix) === 0) {
      path = '/' + path.slice(prefix.length);
    }
    path = path.replace(/\/+$/, '') || '/';

    if (path === '/cart' || path.indexOf('/cart/') === 0) return false;
    if (path === '/checkouts' || path.indexOf('/checkouts/') === 0) return false;
    if (path === '/checkout' || path.indexOf('/checkout/') === 0) return false;

    return true;
  }

  function shouldGoBack() {
    if (!referrerIsUsable()) return false;
    try { if (!(window.history && window.history.length > 1)) return false; } catch (e) { return false; }
    return true;
  }

  // A modified click is the shopper deliberately asking for a new tab/window or
  // a download — leave it entirely to the browser and the href.
  function isPlainLeftClick(event) {
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
  }

  // --- end continue-shopping guards ---

  if (typeof window.HTMLElement === 'function' && typeof window.customElements === 'object') {
    var CartContinueShopping = class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }

      handleClick = (event) => {
        var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
        if (!link || !this.contains(link)) return;
        if (!isPlainLeftClick(event)) return;
        if (!shouldGoBack()) return; // fall through to the href

        event.preventDefault();
        window.history.back();
      };
    };

    if (!window.customElements.get('cart-continue-shopping')) {
      window.customElements.define('cart-continue-shopping', CartContinueShopping);
    }
  }
})();
